import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PagedResult, PagedQuery } from '../types';
import { toast } from 'react-toastify';

interface UsePagedDataOptions {
  initialPage?: number;
  initialPageSize?: number;
  initialSortBy?: string;
  initialSortDesc?: boolean;
}

function parseUrlParams(searchParams: URLSearchParams, defaults: UsePagedDataOptions) {
  const query: PagedQuery = {
    page: Number(searchParams.get('page')) || defaults.initialPage || 1,
    pageSize: Number(searchParams.get('pageSize')) || defaults.initialPageSize || 25,
    sortBy: searchParams.get('sortBy') || defaults.initialSortBy,
    sortDescending: searchParams.has('sortDesc')
      ? searchParams.get('sortDesc') === 'true'
      : defaults.initialSortDesc,
    search: searchParams.get('search') || undefined,
  };
  const filters: Record<string, any> = {};
  searchParams.forEach((value, key) => {
    if (!['page', 'pageSize', 'sortBy', 'sortDesc', 'search'].includes(key)) {
      const num = Number(value);
      filters[key] = !isNaN(num) && value !== '' ? num : value;
    }
  });
  return { query, filters };
}

export function usePagedData<T>(
  fetchFn: (params: any) => Promise<any>,
  options: UsePagedDataOptions = {}
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitial = useRef(true);

  // Parse initial state from URL
  const initialState = parseUrlParams(searchParams, options);

  const [data, setData] = useState<PagedResult<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<PagedQuery>(initialState.query);
  const [filters, setFilters] = useState<Record<string, any>>(initialState.filters);

  // Sync state to URL
  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (query.page && query.page > 1) params.set('page', String(query.page));
    if (query.pageSize && query.pageSize !== 25) params.set('pageSize', String(query.pageSize));
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDescending !== undefined) params.set('sortDesc', String(query.sortDescending));
    if (query.search) params.set('search', query.search);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });

    setSearchParams(params, { replace: true });
  }, [query, filters, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...query, ...filters };
      const response = await fetchFn(params);
      setData(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  }, [fetchFn, query, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setPage = (page: number) => setQuery((q) => ({ ...q, page }));
  const setPageSize = (pageSize: number) => setQuery((q) => ({ ...q, pageSize, page: 1 }));
  const setSort = (sortBy: string) => {
    setQuery((q) => ({
      ...q,
      sortBy,
      sortDescending: q.sortBy === sortBy ? !q.sortDescending : false,
    }));
  };
  const setSearch = (search: string) => setQuery((q) => ({ ...q, search, page: 1 }));

  return {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh: fetchData,
  };
}
